import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownIcon, MailIcon, InstagramIcon, CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { CartModal } from '../components/CartModal';
import { TrainingPlanWarningModal } from '../components/TrainingPlanWarningModal';
import { CountdownTimer } from '../components/CountdownTimer';
import { trackAddToCart } from '../utils/analytics';
import { Button } from '../components/Button';
export const BhsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    addItem,
    cartModalOpen,
    setCartModalOpen,
    lastAddedItem,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  } = useCart();
  const [selectedDuration, setSelectedDuration] = useState<'1' | '3'>('3');
  const [expandedPlan, setExpandedPlan] = useState(false);
  const [openFaqItem, setOpenFaqItem] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleAddToCart = () => {
    const price = selectedDuration === '1' ? 899 : 1799;
    const itemId = `training-beginner-${selectedDuration}`;
    const itemName = `BODY-HACK SYSTÉM (${selectedDuration === '1' ? '1 měsíc' : '3 měsíce'})`;
    trackAddToCart({
      id: itemId,
      name: itemName,
      price: price,
      category: 'training_plan'
    });
    addItem({
      id: itemId,
      name: itemName,
      price,
      type: 'training',
      details: 'Jednorázová platba'
    });
    navigate('/cart2');
  };
  const handleContinueShopping = () => {
    setCartModalOpen(false);
  };
  const handleGoToCart = () => {
    setCartModalOpen(false);
    navigate('/cart2');
  };
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const bulletPoints = ['Videoukázky ke každému cviku (správná technika + tempo)', 'Split: 4 tréninky / 3 dny odpočinku', 'Přesně dané série, opakování a pauzy', 'Systém zapisování a sledování progresu', 'Jasně strukturovaný progresivní overload', 'Využití začátečnického potenciálu pro rychlé výsledky', 'Správná délka pauz mezi sériemi pro maximální růst', 'Plán postavený na základě desítek studií + letech zkušeností', 'Plán, který ti dá know-how na to jak správně cvičit', 'Každé 2 týdny 1:1 online check-up call pro kontrolu tvého progresu'];
  const fullDescription = '🚀 Celý plán je navržený tak, aby si co nejlépe využil tzv. začátečníký potenciál – tedy období, kdy tělo roste nejrychleji a výsledky jdou vidět téměř okamžitě. 📈\n\n🛡️ Zároveň ale dbáme na to, abys ses nepřetrénoval/a – svaly potřebují čas na regeneraci. A právě regeneraci plán zohledňuje, tak aby sis mohl/a budovat formu bezpečně a efektivně. 💪\n\n🤝 Nemusíš se ničeho bát – všechno ti ukážeme krok po kroku. Stačí nabrat odvahu a konečně se rozhodnout se změnit jak fyzicky, tak psychicky. 🧠✨\n\n🔑 Možná právě v tomhle tréninkovém plánu, se skrývá klíč k sebevědomí a změně sebe, po které toužíš. Stačí začít! 🔥';
  const bhsFaqItems = [{
    question: 'Co je BHS – Body Hack System?',
    answer: 'BHS je kompletní systém v aplikaci, který ti krok za krokem ukáže, co přesně cvičit, jak cvičit a jak jíst, aby tvoje tělo začalo růst co nejrychleji.\n\nNemusíš nad ničím přemýšlet — jen následuješ jasný plán.'
  }, {
    question: 'Je tréninkový plán opravdu jednorázová platba?',
    answer: 'Ano. Zaplatíš pouze jednou a získáš přístup k systému na 1 nebo 3 měsíce.\n\nBěhem té doby získáš kompletní know-how, díky kterému už nebudeš muset nikdy platit za další plány nebo trenéry.'
  }, {
    question: 'Na jak dlouho budu mít přístup k plánu?',
    answer: 'Přístup máš na 1 nebo 3 měsíce (dle varianty, kterou si vybereš).\n\n3 měsíce jsou ideální, protože je to dostatečně dlouhá doba na to, aby ses naučil vše potřebné a dokázal se posouvat dál i sám.'
  }, {
    question: 'Budu muset po skončení platit znovu?',
    answer: 'Ne. Žádné opakované platby tě nečekají.\n\nZaplatíš jednou a to, co se naučíš, ti zůstane navždy.'
  }, {
    question: 'Jak probíhají check-up cally?',
    answer: 'Každé 2 týdny máš možnost 1:1 online check-up callu.\n\nDostaneš přístup k rezervačnímu odkazu, kde si jednoduše vybereš termín.\n\nPokud si nebudeš jistý, jestli něco děláš správně, můžeš to se mnou projít a upravit.'
  }, {
    question: 'Je BHS vhodný i pro úplné začátečníky?',
    answer: 'Ano — hlavně pro začátečníky.\n\nSystém je vytvořený tak, aby tě provedl od úplného začátku a odstranil chaos, který většina lidí na začátku má.'
  }, {
    question: 'Co když jsem už zkoušel jiné tréninkové plány?',
    answer: 'To je naprosto v pořádku.\n\nRozdíl je v tom, že BHS není jen plán, ale ucelený systém, který ti vysvětlí, proč věci fungují — takže konečně pochopíš, co děláš.'
  }, {
    question: 'Co všechno v BHS dostanu?',
    answer: 'Získáš:\n\n• přesně rozepsané tréninky\n• videoukázky ke cvikům\n• systém progresu\n• jasně dané série, opakování a pauzy\n• návod, jak správně jíst\n• a celkový systém, který ti dává smysl od začátku do konce'
  }, {
    question: 'Kdy uvidím první výsledky?',
    answer: 'Pokud začneš systém správně aplikovat, první změny můžeš začít vidět už během několika týdnů.\n\nNejde ale jen o rychlost — jde o to, že konečně víš, co děláš.'
  }];
  return <div className="min-h-screen bg-[#0a0a0a] text-white font-sans selection:bg-green-500/30" data-id="element-1498">
      <CartModal isOpen={cartModalOpen} onClose={handleContinueShopping} onGoToCart={handleGoToCart} onContinueShopping={handleContinueShopping} productName={lastAddedItem || ''} data-id="element-1499" />

      <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-1500" />

      {/* Header */}
      <header className="py-6 text-center border-b border-gray-800/50" data-id="element-1501"></header>

      <main className="max-w-2xl mx-auto px-4 py-8 md:py-12" data-id="element-1502">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-1503">
          {/* Top Image - Chceš stejné výsledky? */}
          <div className="relative rounded-2xl overflow-hidden mb-6 shadow-2xl shadow-black/50 ring-1 ring-white/10" data-id="element-1504">
            <img src="https://cdn.magicpatterns.com/uploads/jgZdjsB2yKGafZqGnbNtn2/openart-image_CUYi3l9r_1773680450002_raw.jpg" alt="Chceš stejné výsledky?" className="w-full h-auto object-cover" data-id="element-1505" />
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8 shadow-2xl shadow-black/50 ring-1 ring-white/10" data-id="element-1506"></div>

          {/* Headline */}
          <div className="space-y-6 mb-10" data-id="element-1507">
            <h1 className="text-2xl md:text-3xl font-bold leading-tight text-white" data-id="element-1508">
              Tohle je poslední tréninkový systém, který budeš ve svém{' '}
              <span style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-green-500 italic" data-id="element-1509">
                životě potřebovat
              </span>
              , abys konečně začal vidět výsledky, po kterých toužíš. 🚀
            </h1>
          </div>

          {/* Video */}
          <div className="mb-12 relative w-full aspect-video rounded-2xl overflow-hidden shadow-xl border border-gray-800" data-id="element-1510">
            <iframe src="https://www.veed.io/embed/3e707a09-8695-4352-b67e-ea13a36c449f?watermark=0&color=default&sharing=1&title=0" className="absolute inset-0 w-full h-full" frameBorder="0" title="BHS Video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-id="element-1511" />
          </div>

          {/* Sales Copy */}
          <div className="space-y-6 mb-12 text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-1512">
            <p data-id="element-1513">
              Možná už nějakou dobu přemýšlíš, že začneš cvičit. Možná jsi
              dokonce už párkrát chtěl začít, ale nakonec jsi to odložil. Ne
              proto, že bys nechtěl{' '}
              <strong className="text-white" data-id="element-1514">změnit své tělo</strong>… ale
              protože vlastně vůbec{' '}
              <strong className="text-white" data-id="element-1515">nevíš, kde začít</strong>. 🤔
            </p>
            <p data-id="element-1516">
              Možná máš pocit, že{' '}
              <strong className="text-white" data-id="element-1517">nevíš, jaké cviky dělat</strong>,{' '}
              <strong className="text-white" data-id="element-1518">
                jak správně cvičit techniku
              </strong>{' '}
              nebo jestli vůbec{' '}
              <strong className="text-white" data-id="element-1519">děláš věci správně</strong>. A
              někde vzadu v hlavě máš i ten{' '}
              <strong className="text-white" data-id="element-1520">nepříjemný pocit</strong>, že když
              přijdeš do posilovny, budeš vypadat jako někdo, kdo{' '}
              <strong className="text-white" data-id="element-1521">vůbec neví, co dělá</strong>.
            </p>
            <p data-id="element-1522">
              A víš co?{' '}
              <strong className="text-white" data-id="element-1523">
                Tenhle pocit má na začátku skoro každý
              </strong>
              . 🙌
            </p>
            <p data-id="element-1524">
              Právě proto vznikl{' '}
              <strong className="text-white" data-id="element-1525">BHS – Body Hack System</strong>,
              aby tě zbavil všech těchto obav a problémů! 💪
            </p>
            <p data-id="element-1526">
              Je to <strong className="text-white" data-id="element-1527">kompletní systém</strong>,
              který ti <strong className="text-white" data-id="element-1528">krok za krokem</strong>{' '}
              ukáže, <strong className="text-white" data-id="element-1529">jak přesně cvičit</strong>,
              jaké cviky dělat, kolik opakování provádět a jak si nastavit
              tréninky tak, aby tvoje tělo{' '}
              <strong className="text-white" data-id="element-1530">konečně začalo růst</strong>.{' '}
              <strong className="text-white" data-id="element-1531">Nemusíš přemýšlet</strong> nad tím,
              jestli něco děláš špatně nebo jestli bys měl něco změnit.{' '}
              <strong className="text-white" data-id="element-1532">Stačí přijít do posilovny</strong>{' '}
              a <strong className="text-white" data-id="element-1533">následovat systém</strong>, který
              už{' '}
              <strong className="text-white" data-id="element-1534">pomohl mně i mým klientům</strong>{' '}
              dosáhnout{' '}
              <strong className="text-white" data-id="element-1535">skutečných výsledků</strong> — a
              teď může pomoci i tobě docílit tvé vysněné postavy! 🔥
            </p>
          </div>

          {/* Animated Arrow & CTA */}
          <div className="flex flex-col items-center mb-16" data-id="element-1536">
            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="mb-4" data-id="element-1537">
              <ArrowDownIcon className="w-8 h-8 text-green-500" data-id="element-1538" />
            </motion.div>
            <button onClick={scrollToPricing} className="w-full bg-green-500 hover:bg-green-400 text-black font-black text-xl py-5 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.5)] active:scale-[0.98]" data-id="element-1539">
              CENÍK BHS!
            </button>
          </div>

          {/* Pricing Section */}
          <div id="pricing-section" className="pt-8 md:max-w-md md:mx-auto" data-id="element-1540">
            <CountdownTimer data-id="element-1541" />

            <motion.div className="premium-card flex flex-col h-full bg-gray-900/80 border border-gray-800 rounded-2xl overflow-hidden mt-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} whileHover={{
            y: -5,
            boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.3)',
            borderColor: 'rgba(34, 197, 94, 0.3)'
          }} data-id="element-1542">
              <div className="p-3 md:p-4 border-b border-gray-800 relative" data-id="element-1543">
                <div className="flex items-center justify-between" data-id="element-1544">
                  <h3 className="text-base md:text-lg font-black text-green-500 flex items-center" data-id="element-1545">
                    BHS SYSTÉM
                  </h3>
                  <AnimatePresence data-id="element-1546">
                    {selectedDuration === '3' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.8
                  }} transition={{
                    duration: 0.2
                  }} className="relative ml-3" data-id="element-1547">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-500 rounded-full blur-sm opacity-60 animate-pulse" data-id="element-1548"></div>
                        <div className="relative bg-gradient-to-r from-green-600 to-green-500 text-white text-[9px] md:text-[10px] font-black px-2.5 md:px-3 py-1 md:py-1 rounded-full shadow-lg border border-white/30" data-id="element-1549">
                          <span className="relative z-10 tracking-wider whitespace-nowrap" data-id="element-1550">
                            NEJPRODÁVANĚJŠÍ
                          </span>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="p-3 md:p-4 flex-1 flex flex-col" data-id="element-1551">
                <div className="flex-1 flex flex-col" data-id="element-1552">
                  <ul className="space-y-1.5 md:space-y-2 mb-3 md:mb-4 flex-1" data-id="element-1553">
                    {bulletPoints.map((point, idx) => <li key={idx} className="flex items-start gap-2 md:gap-2.5" data-id="element-1554">
                        <div className="flex-shrink-0 mt-0.5" data-id="element-1555">
                          <CheckIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-500" strokeWidth={2.5} data-id="element-1556" />
                        </div>
                        <span className="text-gray-200 text-xs md:text-sm font-medium leading-relaxed" data-id="element-1557">
                          {point}
                        </span>
                      </li>)}
                  </ul>

                  <motion.button onClick={() => setExpandedPlan(!expandedPlan)} className="flex items-center text-green-500 hover:text-green-400 transition-colors text-xs md:text-xs font-bold mb-3 md:mb-4" whileHover={{
                  x: 5
                }} transition={{
                  duration: 0.2
                }} data-id="element-1558">
                    {expandedPlan ? <>
                        <span data-id="element-1559">MÉNĚ INFORMACÍ</span>
                        <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-1560" />
                      </> : <>
                        <span data-id="element-1561">VÍCE INFORMACÍ</span>
                        <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1562" />
                      </>}
                  </motion.button>

                  <motion.div initial={false} animate={{
                  height: expandedPlan ? 'auto' : 0,
                  opacity: expandedPlan ? 1 : 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1563">
                    {expandedPlan && <div className="mt-1 text-gray-300 bg-gray-800/50 p-4 rounded-xl text-xs md:text-sm mb-4 leading-relaxed border border-gray-700/50" data-id="element-1564">
                        <p className="whitespace-pre-line" data-id="element-1565">{fullDescription}</p>
                      </div>}
                  </motion.div>

                  <div className="flex justify-center mb-3 md:mb-4 bg-gray-800/80 rounded-lg md:rounded-lg overflow-hidden p-1 border border-gray-700/50" data-id="element-1566">
                    <button onClick={() => setSelectedDuration('1')} className={`flex-1 py-2 md:py-2 text-center font-bold transition-all duration-300 text-xs md:text-sm rounded-md md:rounded-md ${selectedDuration === '1' ? 'bg-gray-700 text-white shadow-md' : 'bg-transparent text-gray-400 hover:text-gray-200'}`} data-id="element-1567">
                      1 měsíc
                    </button>
                    <button onClick={() => setSelectedDuration('3')} className={`flex-1 py-2 md:py-2 text-center font-bold transition-all duration-300 text-xs md:text-sm rounded-md md:rounded-md ${selectedDuration === '3' ? 'bg-green-600 text-white shadow-md shadow-green-900/20' : 'bg-transparent text-gray-400 hover:text-gray-200'}`} data-id="element-1568">
                      3 měsíce
                    </button>
                  </div>

                  <div className="text-center mb-3 md:mb-5" data-id="element-1569">
                    <div className="text-2xl md:text-4xl font-black text-green-500 mb-1.5 md:mb-2 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)]" data-id="element-1570">
                      {selectedDuration === '1' ? '899 Kč' : '1799 Kč'}
                    </div>

                    {selectedDuration === '3' && <div className="relative inline-block bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/20 rounded-lg px-3 md:px-4 py-1 md:py-1.5 mb-1.5 md:mb-2 backdrop-blur-sm" data-id="element-1571">
                        <div className="text-xs md:text-sm text-gray-300 flex items-center justify-center gap-2 md:gap-2.5" data-id="element-1572">
                          <span className="text-gray-500 line-through decoration-red-500/40 decoration-1" data-id="element-1573">
                            2697 Kč
                          </span>
                          <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 uppercase tracking-wide" data-id="element-1574">
                            Ušetříš 898 Kč
                          </span>
                        </div>
                      </div>}

                    <div className="flex items-center justify-center gap-2 text-xs md:text-sm mt-1 md:mt-1.5" data-id="element-1575">
                      <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-300 to-green-400 tracking-wide drop-shadow-[0_0_8px_rgba(34,197,94,0.4)]" data-id="element-1576">
                        Jednou zaplatíš a už nikdy nebudeš muset platit znovu!
                      </span>
                    </div>
                  </div>

                  <button onClick={handleAddToCart} className="w-full bg-green-600 text-white py-3 md:py-3.5 rounded-lg md:rounded-xl font-black text-sm md:text-base flex items-center justify-center hover:bg-green-500 transition-all duration-300 shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_30px_rgba(34,197,94,0.5)] active:scale-[0.98]" data-id="element-1577">
                    <CheckIcon className="w-4 h-4 md:w-5 md:h-5 mr-2" strokeWidth={3} data-id="element-1578" />
                    PŘIDAT DO KOŠÍKU
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* BHS FAQ Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="mt-16 md:mt-20 md:max-w-md md:mx-auto" data-id="element-1579">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 border border-green-500/30 shadow-[0_10px_40px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-1580">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1581"></div>
              <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1582"></div>
              <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-1583"></div>

              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center relative" data-id="element-1584">
                <span className="relative inline-block" data-id="element-1585">
                  <span className="relative z-10" data-id="element-1586">
                    FAQ –{' '}
                    <span className="text-green-500" data-id="element-1587">Body Hack System</span>
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-1588"></span>
                </span>
              </h2>

              <div className="space-y-4" data-id="element-1589">
                {bhsFaqItems.map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }} viewport={{
                once: true
              }} className="bg-black/30 rounded-lg border border-green-500/20 overflow-hidden hover:border-green-500/40 transition-all duration-300" data-id="element-1590">
                    <button onClick={() => setOpenFaqItem(openFaqItem === index ? null : index)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-green-500/5 transition-colors duration-300" data-id="element-1591">
                      <span className="text-base md:text-lg font-medium text-white pr-4" data-id="element-1592">
                        {item.question}
                      </span>
                      <motion.div animate={{
                    rotate: openFaqItem === index ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} className="flex-shrink-0" data-id="element-1593">
                        <ChevronDownIcon className="w-5 h-5 text-green-500" data-id="element-1594" />
                      </motion.div>
                    </button>

                    <AnimatePresence data-id="element-1595">
                      {openFaqItem === index && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-1596">
                          <div className="px-6 pb-4 pt-2" data-id="element-1597">
                            <div className="text-sm md:text-base text-gray-300 leading-relaxed whitespace-pre-line" data-id="element-1598">
                              {item.answer}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div className="mt-20 mb-12" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} data-id="element-1599">
            <div className="text-center mb-8" data-id="element-1600">
              <h2 className="text-xl md:text-2xl font-black tracking-tight leading-relaxed" data-id="element-1601">
                MÁŠ NĚJAKÉ <span className="text-green-500" data-id="element-1602">OTÁZKY</span>?{' '}
                <span className="text-green-500" data-id="element-1603">NAPIŠ MI</span>, NEBO SI{' '}
                <span className="text-green-500" data-id="element-1604">ZAREZERVUJ</span> SVŮJ ONLINE
                MEETING <span className="text-green-500" data-id="element-1605">ZDARMA</span>!
              </h2>
              <div className="w-16 h-0.5 bg-gradient-to-r from-green-500/0 via-green-500 to-green-500/0 mx-auto mt-4" data-id="element-1606"></div>
            </div>

            <div className="space-y-4" data-id="element-1607">
              {/* Meeting CTA - First */}
              <motion.a href="https://calendly.com/ondrej-janik-body-hack/15-minutovy-meeting-otazky" target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} viewport={{
              once: true
            }} whileHover={{
              y: -4,
              boxShadow: '0 16px 40px -8px rgba(34, 197, 94, 0.3)'
            }} className="group relative block bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl p-7 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all duration-500 overflow-hidden shadow-lg shadow-black/20" data-id="element-1608">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" data-id="element-1609"></div>
                <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/10 to-transparent" data-id="element-1610"></div>
                <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-500/5 rounded-full filter blur-2xl group-hover:bg-green-500/10 transition-all duration-500" data-id="element-1611"></div>
                <div className="flex flex-col items-center text-center" data-id="element-1612">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500/15 to-green-500/5 rounded-full flex items-center justify-center mb-3.5 group-hover:from-green-500/25 group-hover:to-green-500/10 transition-all duration-500 ring-1 ring-green-500/20 group-hover:ring-green-500/40" data-id="element-1613">
                    <CalendarIcon className="w-5 h-5 text-green-500" data-id="element-1614" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2" data-id="element-1615">
                    Online Meeting
                  </span>
                  <span className="text-green-400 text-sm font-bold tracking-wide" data-id="element-1616">
                    ZAREZERVUJ SI MEETING ZDARMA!
                  </span>
                </div>
              </motion.a>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1617">
                {/* Email */}
                <motion.a href="mailto:ondrej.janik@body-hack.cz" initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.2
              }} viewport={{
                once: true
              }} whileHover={{
                y: -4,
                boxShadow: '0 16px 40px -8px rgba(34, 197, 94, 0.3)'
              }} className="group relative bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl p-7 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all duration-500 overflow-hidden shadow-lg shadow-black/20" data-id="element-1618">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" data-id="element-1619"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/10 to-transparent" data-id="element-1620"></div>
                  <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-500/5 rounded-full filter blur-2xl group-hover:bg-green-500/10 transition-all duration-500" data-id="element-1621"></div>
                  <div className="flex flex-col items-center text-center" data-id="element-1622">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/15 to-green-500/5 rounded-full flex items-center justify-center mb-3.5 group-hover:from-green-500/25 group-hover:to-green-500/10 transition-all duration-500 ring-1 ring-green-500/20 group-hover:ring-green-500/40" data-id="element-1623">
                      <MailIcon className="w-5 h-5 text-green-500" data-id="element-1624" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2" data-id="element-1625">
                      Email
                    </span>
                    <span className="text-green-400 text-sm font-bold break-all" data-id="element-1626">
                      ondrej.janik@body-hack.cz
                    </span>
                  </div>
                </motion.a>

                {/* Instagram */}
                <motion.a href="https://www.instagram.com/ondra.janikk/" target="_blank" rel="noopener noreferrer" initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.3
              }} viewport={{
                once: true
              }} whileHover={{
                y: -4,
                boxShadow: '0 16px 40px -8px rgba(34, 197, 94, 0.3)'
              }} className="group relative bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-xl p-7 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all duration-500 overflow-hidden shadow-lg shadow-black/20" data-id="element-1627">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" data-id="element-1628"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/10 to-transparent" data-id="element-1629"></div>
                  <div className="absolute -right-16 -top-16 w-32 h-32 bg-green-500/5 rounded-full filter blur-2xl group-hover:bg-green-500/10 transition-all duration-500" data-id="element-1630"></div>
                  <div className="flex flex-col items-center text-center" data-id="element-1631">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/15 to-green-500/5 rounded-full flex items-center justify-center mb-3.5 group-hover:from-green-500/25 group-hover:to-green-500/10 transition-all duration-500 ring-1 ring-green-500/20 group-hover:ring-green-500/40" data-id="element-1632">
                      <InstagramIcon className="w-5 h-5 text-green-500" data-id="element-1633" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2" data-id="element-1634">
                      Instagram
                    </span>
                    <span className="text-green-400 text-sm font-bold" data-id="element-1635">
                      @ondra.janikk
                    </span>
                  </div>
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Footer Link */}
          <div className="text-center pb-8 mt-8" data-id="element-1636">
            <a href="#" className="text-sm text-gray-500 hover:text-gray-300 underline transition-colors" data-id="element-1637">
              Privacy Policy
            </a>
          </div>
        </motion.div>
      </main>
    </div>;
};